import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./guidedInstallModal.html";
import { aiAssetEngagement, INSTALL_METHOD_LABELS } from '~/modules/analytics/gtmEventTypes/aiAssetAnalyticsContract';
import { copyLinkToClipboard } from '~/modules/helpers/shareLinkHelper';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { isPluginListing } from '~/modules/utilities/listing_utils';
import { sendNotification } from '~/modules/utilities/notificationUtilities';
import { getClaudePluginInstallCommands } from '~/modules/utilities/plugin_utils';
import { buildSkillsInstallCommand, buildSkillsListCommand, getSkillSlug, getSkillSource } from '~/modules/utilities/skill_utils';

// Matches the copy-to-clipboard confirmation timeout in mfe/share/share.ts — long enough to read
// a short toast, short enough to not linger after the modal has already closed.
const COPIED_NOTIFICATION_AUTO_DISMISS_MS = 3000;

// The one install method with no CLI path — same id the sidebar's `isAutoInstalled` checks (skills
// install automatically in Agentforce Vibes, via its own "+ Add Skill" UI instead of a command).
const AUTO_INSTALLED_METHOD_ID = 'agentforce-vibes';

// Opens Claude Desktop's skill upload window. The browser gives no signal whether the app is
// installed, so the desktop dialog always offers the ZIP download as the fallback.
const CLAUDE_DESKTOP_SKILL_UPLOAD_URL = 'claude://claude.ai/customize/skills/new/upload';

// How long the desktop dialog shows its "Attempting to open" status before assuming the app did not
// open and offering Try again alongside the ZIP fallback (Figma "Add to Claude" retry state).
const CLAUDE_DESKTOP_RETRY_DELAY_MS = 3000;
const CLAUDE_SKILLS_HELP_URL = 'https://support.claude.com/en/articles/12512180-use-skills-in-claude#h_a4222fa77b';
/**
 * Dialog opened by the "Get Install Prompt" button in the skill install box
 * (Figma node 1708:29412). Shows the verbatim install prompt in a scrollable,
 * read-only code block and a footer button that copies it to the clipboard.
 *
 * Follows the V2 fork convention (`listing/metadataCardV2`): a standalone
 * component taking `@api listing`, not a subclass of any v1 component. Uses
 * the site's `wds-modal` rather than the design system's native-dialog modal;
 * Safari can paint the native backdrop without painting its slotted content.
 */
class GuidedInstallModal extends LightningElement {
  constructor(...args) {
    super(...args);
    this.listing = void 0;
    // The Command Line tab's currently-selected `-a` target, passed down from `skillSidebar`/
    // `pluginSidebar` (`selectedInstallMethodId`) so the copied command is already fully-rendered —
    // no placeholder left for the agent to fill in. Reactive: the sidebar re-renders this modal
    // whenever the dropdown selection changes, same as any other `@api` prop.
    this.installMethodId = 'claude-code';
    // 'prompt' renders the copyable install prompt; 'cli' renders the bare install command
    // (Figma "CLI Script for {harness}"); 'desktop' opens Claude Desktop with a ZIP fallback
    // (Figma "Add to Claude Desktop"); 'zip' shows the Claude upload steps after the sidebar's
    // Download ZIP starts (Figma "Install in Claude"). The skill sidebar mounts one instance of each.
    this.mode = 'prompt';
    // The selected harness's published ZIP, offered by the desktop dialog's Download ZIP button.
    this.zipUrl = '';
    this.i18n = void 0;
    this.isOpen = false;
    this.commandExpanded = false;
    this.desktopRetryVisible = false;
    this.topLayerUnavailable = false;
  }
  disconnectedCallback() {
    this.clearDesktopRetryTimer();
  }
  renderedCallback() {
    if (!this.isOpen || this.activeTopLayer || this.topLayerUnavailable) {
      return;
    }
    const topLayer = this.template?.querySelector('.guided-install-top-layer');
    if (!topLayer || typeof topLayer.showPopover !== 'function') {
      // Older browsers treat the unknown `popover` attribute as inert, leaving the wrapper
      // and its existing fixed-position WDS modal visible as the graceful fallback.
      if (this.isZipMode) {
        // Zip mode turns off wds-modal's own autofocus, so place initial focus here once.
        this.topLayerUnavailable = true;
        this.template?.querySelector('.initial-focus')?.focus();
      }
      return;
    }
    try {
      topLayer.showPopover();
      this.activeTopLayer = topLayer;
      // wds-modal renders while its popover ancestor is still display:none, so its normal
      // initial-focus attempt cannot land. Once promoted, focus the same scrollable prompt
      // region that is second in the modal's focus order; WDS continues to trap focus.
      this.template?.querySelector('.initial-focus')?.focus();
    } catch {
      // If the browser exposes a partial/broken Popover implementation, make the wrapper
      // visible normally rather than leaving the install UI hidden by the UA popover rule.
      this.topLayerUnavailable = true;
      topLayer.removeAttribute('popover');
      this.template?.querySelector('.initial-focus')?.focus();
    }
  }
  show(closedFocusTarget) {
    this.topLayerUnavailable = false;
    this.closedFocusTarget = closedFocusTarget;
    this.commandExpanded = false;
    this.isOpen = true;
    if (this.isDesktopMode) {
      this.openClaudeDesktop();
    }
  }
  close() {
    if (this.activeTopLayer && typeof this.activeTopLayer.hidePopover === 'function') {
      try {
        this.activeTopLayer.hidePopover();
      } catch {
        // Removing the wrapper in the render below is also sufficient to leave the top
        // layer; tolerate an already-closed popover so modal cleanup and focus restoration
        // still complete.
      }
    }
    this.activeTopLayer = undefined;
    this.clearDesktopRetryTimer();
    this.desktopRetryVisible = false;
    this.isOpen = false;
    this.closedFocusTarget?.focus();
    this.closedFocusTarget = undefined;
  }
  handleModalClose() {
    this.close();
  }

  // The shared focus trap ignores this tabindex="-1" status line and would send Tab to the close
  // button; let the browser move focus in reading order (help link next, close button before).
  handleZipStatusKeydown(event) {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }
  handleDone() {
    this.close();
  }
  handleTryAgain() {
    // Keep keyboard focus inside the dialog before the focused retry button unmounts.
    this.template?.querySelector('.desktop-status')?.focus();
    this.openClaudeDesktop();
  }

  // The browser gives no signal whether the app opened, so the status always reverts to the
  // attempting state and the retry offer reappears after the same delay.
  openClaudeDesktop() {
    this.clearDesktopRetryTimer();
    this.desktopRetryVisible = false;
    window.location.assign(CLAUDE_DESKTOP_SKILL_UPLOAD_URL);
    this.desktopRetryTimer = setTimeout(() => {
      this.desktopRetryTimer = undefined;
      this.desktopRetryVisible = true;
    }, CLAUDE_DESKTOP_RETRY_DELAY_MS);
  }
  clearDesktopRetryTimer() {
    if (this.desktopRetryTimer) {
      clearTimeout(this.desktopRetryTimer);
      this.desktopRetryTimer = undefined;
    }
  }
  get isCliMode() {
    return this.mode === 'cli';
  }
  get isDesktopMode() {
    return this.mode === 'desktop';
  }

  /** wds-modal re-focuses its second focusable element whenever it re-renders (e.g. when its
   *  label wire resolves), which in zip mode is the help link, so zip mode keeps initial focus
   *  on the download-ready status instead. */
  get isZipMode() {
    return this.mode === 'zip';
  }

  /** Desktop and zip modes have no prompt box to absorb a short viewport, so their content
   *  scrolls instead. */
  get modalClass() {
    return this.isZipMode ? 'zip-modal' : '';
  }
  get mainContentClass() {
    return this.isDesktopMode || this.isZipMode ? 'main-content main-content--desktop' : 'main-content';
  }
  get modalTitle() {
    if (this.isZipMode) {
      return this.i18n?.skills?.installInClaudeModalTitle ?? '';
    }
    if (this.isDesktopMode) {
      return this.i18n?.skills?.addToClaudeDesktop ?? '';
    }
    if (this.isCliMode) {
      return i18nStore.getState().i18n.skills.cliScriptModalTitle({
        PRODUCT: this.productName
      });
    }
    if (this.isPlugin) {
      return this.i18n?.skills?.guidedInstallModalTitle ?? '';
    }
    return i18nStore.getState().i18n.skills.installPromptModalTitle({
      PRODUCT: this.productName
    });
  }

  /** Takes a `{PRODUCT}` param (the selected install method's display name), so — like
   *  `promptText` below — it must go through the raw `i18nStore` access proxy rather than the
   *  wired static-cache `i18n` proxy, which always calls message functions with no arguments. */
  get subtitle() {
    if (this.isCliMode) {
      return (this.isPlugin ? this.i18n?.skills?.cliScriptModalSubtitlePlugin : this.i18n?.skills?.cliScriptModalSubtitle) ?? '';
    }
    return i18nStore.getState().i18n.skills.guidedInstallModalSubtitle({
      PRODUCT: this.productName
    });
  }
  get copyButtonLabel() {
    return this.i18n?.skills?.guidedInstallModalCopy ?? '';
  }
  get copyButtonName() {
    return this.isCliMode ? 'copy-install-command' : 'copy-install-prompt';
  }
  get desktopOpeningLabel() {
    return this.i18n?.skills?.claudeDesktopModalOpening ?? '';
  }
  get desktopRetryHeading() {
    return this.i18n?.skills?.claudeDesktopModalRetryHeading ?? '';
  }
  get tryAgainLabel() {
    return this.i18n?.skills?.claudeDesktopModalTryAgain ?? '';
  }
  get desktopBody() {
    return this.i18n?.skills?.claudeDesktopModalBody ?? '';
  }
  get desktopFallback() {
    return this.i18n?.skills?.claudeDesktopModalFallback ?? '';
  }
  get zipReadyHeading() {
    return this.i18n?.skills?.installInClaudeModalReady ?? '';
  }
  get zipInstallSteps() {
    const skills = this.i18n?.skills;
    return [skills?.installInClaudeModalStepCustomize, skills?.installInClaudeModalStepAdd, skills?.installInClaudeModalStepUpload].map((text, index) => ({
      id: `zip-step-${index}`,
      text: text ?? ''
    }));
  }
  get skillsHelpLabel() {
    return this.i18n?.skills?.installInClaudeModalHelpLink ?? '';
  }
  get skillsHelpUrl() {
    return CLAUDE_SKILLS_HELP_URL;
  }
  get desktopUploadPrompt() {
    return this.i18n?.skills?.installInClaudeModalDesktopPrompt ?? '';
  }
  get desktopUploadLabel() {
    return this.i18n?.skills?.installInClaudeModalDesktopLink ?? '';
  }
  get desktopUploadUrl() {
    return CLAUDE_DESKTOP_SKILL_UPLOAD_URL;
  }
  get doneLabel() {
    return this.i18n?.skills?.installInClaudeModalDone ?? '';
  }
  get downloadZipLabel() {
    return this.i18n?.skills?.downloadZip ?? '';
  }
  get npxNoteMessage() {
    return this.i18n?.skills?.cliScriptModalNpxNote ?? '';
  }

  /** Plugins install through the `claude` CLI, not npx. */
  get showNpxNote() {
    return !this.isPlugin;
  }
  get showFullCommandLabel() {
    return (this.commandExpanded ? this.i18n?.skills?.hideFullCommand : this.i18n?.skills?.showFullCommand) ?? '';
  }
  get commandAriaExpanded() {
    return this.commandExpanded ? 'true' : 'false';
  }
  get commandClasses() {
    return this.commandExpanded ? 'command expanded' : 'command';
  }
  get copiedNotificationMessage() {
    return this.i18n?.skills?.guidedInstallModalCopiedNotification ?? '';
  }
  get copyFailureMessage() {
    return this.i18n?.error?.userActionFailure?.description ?? '';
  }

  /** True for a plugin listing — selects its pinned source folder and installs every skill found
   *  beneath it. Same check `skillPackageFiles.ts` uses. */
  get isPlugin() {
    return isPluginListing(this.listing);
  }

  /** The noun for this listing's type — 'skill' or 'plugin' — filling the prompt's {ITEM}
   *  placeholder so the copy names the exact thing being installed instead of the ambiguous
   *  "skill/plugin" (we always know which it is here). Sourced from i18n like the other prompt
   *  fragments, so it stays translator-owned. */
  get itemNoun() {
    return this.isPlugin ? this.i18n?.skills?.guidedInstallItemPlugin ?? '' : this.i18n?.skills?.guidedInstallItemSkill ?? '';
  }

  /** The skill's canonical CLI slug, shared with `skillSidebar`. Plugins are installed by their
   *  authored Claude marketplace specifier instead of a source-folder slug. */
  get skillSlug() {
    return getSkillSlug(this.listing);
  }

  /** True when the sidebar's currently-selected install method is Agentforce Vibes — same
   *  `isAutoInstalled` check `skillSidebar`/`pluginSidebar` use to hide their own command box,
   *  since Vibes has no CLI path (skills install automatically there). */
  get isAutoInstalled() {
    return this.installMethodId === AUTO_INSTALLED_METHOD_ID;
  }

  /** Tool name of the currently-selected install method, for the `{PRODUCT}` placeholder in the
   *  subtitle and prompt below. Deliberately the specific tool ("Claude Code", "Codex") rather
   *  than the dropdown's product-family label ("Claude", "ChatGPT"): the prompt matches it
   *  against the skills CLI's `agents` list, which reports tool names. */
  get productName() {
    const labels = {
      'agentforce-vibes': this.i18n?.skills?.installMethodAgentforceVibes,
      'claude-code': this.i18n?.skills?.installMethodClaudeCode,
      cursor: this.i18n?.skills?.installMethodCursor,
      codex: this.i18n?.skills?.installMethodCodex,
      opencode: this.i18n?.skills?.installMethodOpenCode
    };
    return labels[this.installMethodId] ?? '';
  }

  /** The exact shell command shown in CLI mode: the skills CLI command for a skill, Claude's
   *  marketplace command for a plugin (plugin prompts use Claude's native slash command instead).
   *  '' when the selected listing/method cannot produce a safe command. */
  get installCommand() {
    if (this.isPlugin) {
      return getClaudePluginInstallCommands(this.listing)?.cli ?? '';
    }
    if (this.isAutoInstalled) {
      return '';
    }
    return buildSkillsInstallCommand(getSkillSource(this.listing), this.skillSlug, this.installMethodId);
  }

  /** Global-scope counterpart to installCommand. The skills CLI defaults to project scope and
   *  does not reliably ask interactively once the source, skill/plugin, and agent are specified,
   *  so the global choice must be encoded explicitly instead of delegated to a later prompt. */
  get globalInstallCommand() {
    return !this.isPlugin && this.installCommand ? `${this.installCommand} --global` : '';
  }

  /** Bounded project/global preflight commands. Using the Skills CLI avoids asking the pasted-in
   *  agent to guess install directories or recursively scan the user's home directory. */
  get projectListCommand() {
    return !this.isPlugin && !this.isAutoInstalled ? buildSkillsListCommand(this.installMethodId) : '';
  }
  get globalListCommand() {
    return !this.isPlugin && !this.isAutoInstalled ? buildSkillsListCommand(this.installMethodId, true) : '';
  }

  /** The fully-rendered step-2 text — the one step that branches on the selected install method.
   *  Agentforce Vibes has no CLI path, so it points the agent at the "+ Add Skill" UI instead of
   *  a command; every other method gets the exact installCommand above. Two separate i18n keys
   *  (rather than one key with conditional JS-built prose) so both variants stay fully
   *  translator-owned in en.json. The CLI variant receives both fully-rendered scope commands so
   *  the pasted prompt never asks an agent to infer where `--global` belongs. */
  get installStepText() {
    if (this.isAutoInstalled) {
      return i18nStore.getState().i18n.skills.guidedInstallPromptStepVibes({
        ITEM: this.itemNoun
      });
    }
    return i18nStore.getState().i18n.skills.guidedInstallPromptStepCli({
      PROJECT_COMMAND: this.installCommand,
      GLOBAL_COMMAND: this.globalInstallCommand,
      PROJECT_LIST_COMMAND: this.projectListCommand,
      GLOBAL_LIST_COMMAND: this.globalListCommand,
      SKILL_NAME: this.skillSlug,
      PRODUCT: this.productName
    });
  }

  /** Resolved prompt text, used both for display in the code block and as the string copied to
   *  the clipboard, so the two are always in sync.
   *
   *  `guidedInstallPrompt` takes `{ITEM}`/`{PRODUCT}`/`{INSTALL_STEP}` params, so it must be called
   *  through the raw `i18nStore` access proxy (which invokes message functions with the caller's
   *  arguments) — not through the wired `i18n` static-cache proxy, which always calls message
   *  functions with no arguments and throws for ones that require them (see
   *  `metadataCardV2.reviewsCountLabel` for the same pattern). */
  get promptText() {
    if (this.isPlugin) {
      // Keep the slash command as the entire clipboard payload. A leading prose wrapper would
      // stop Claude Code from recognizing it as a native `/plugin install` command.
      return getClaudePluginInstallCommands(this.listing)?.inClaudeCode ?? '';
    }
    return i18nStore.getState().i18n.skills.guidedInstallPrompt({
      ITEM: this.itemNoun,
      PRODUCT: this.productName,
      INSTALL_STEP: this.installStepText
    });
  }

  /** What the footer button copies: the bare command in CLI mode, otherwise the prompt. */
  get copyText() {
    return this.isCliMode ? this.installCommand : this.promptText;
  }
  handleToggleCommand() {
    this.commandExpanded = !this.commandExpanded;
  }
  async handleCopy() {
    const response = await copyLinkToClipboard(this.copyText);
    if (!response.isSuccess) {
      sendNotification(this.copyFailureMessage, {
        exceptionInfo: response.exceptionInfo
      });
      return;
    }
    this.close();
    // 'copy' matches the footer button's own icon, so the toast echoes the action it confirms.
    sendNotification(this.copiedNotificationMessage, {
      icon: 'copy',
      autoDismissMs: COPIED_NOTIFICATION_AUTO_DISMISS_MS
    });
  }

  // Runs after wds-button's own internal handler has already stamped event.detail.button — just
  // adds which install method the copied prompt was rendered for.
  handleAnalytics(event) {
    event.detail.installMethod = this.installMethodId;
    // CLI mode replaces the sidebar's inline command field, so its copy and show-full clicks
    // carry the same engagement that wds-install-command emitted.
    if (this.isCliMode) {
      event.detail.aiAssetAnalytics = this.cliEngagement('Copy Command', 'button');
    }
  }
  handleDownloadZipAnalytics(event) {
    event.detail.installMethod = this.installMethodId;
    event.detail.aiAssetAnalytics = this.engagement('Download ZIP', 'button', 'ZIP Download', this.zipUrl);
  }
  handleSkillsHelpAnalytics(event) {
    event.detail.installMethod = this.installMethodId;
    event.detail.aiAssetAnalytics = this.engagement('Learn more about Claude skills', 'link text', 'ZIP Download', CLAUDE_SKILLS_HELP_URL);
  }
  handleDesktopUploadAnalytics(event) {
    event.detail.installMethod = this.installMethodId;
    event.detail.aiAssetAnalytics = this.engagement('Open the upload settings', 'link text', 'ZIP Download', CLAUDE_DESKTOP_SKILL_UPLOAD_URL);
  }
  handleDoneAnalytics(event) {
    event.detail.installMethod = this.installMethodId;
    event.detail.aiAssetAnalytics = this.engagement('Done', 'button', 'ZIP Download');
  }
  handleTryAgainAnalytics(event) {
    event.detail.installMethod = this.installMethodId;
    event.detail.aiAssetAnalytics = this.engagement('Try Again', 'button', 'ZIP Download');
  }
  handleToggleCommandAnalytics(event) {
    // wds-button emits the analytics event before the click flips `commandExpanded`, so the
    // current state describes the action the user is taking next.
    event.detail.elementType = 'marketplaceCommandToggle';
    event.detail.aiAssetAnalytics = this.cliEngagement(this.commandExpanded ? 'Hide Full Command' : 'Show Full Command', 'link text');
  }
  cliEngagement(clickText, elementType) {
    return this.engagement(clickText, elementType, 'CLI Script');
  }
  engagement(clickText, elementType, installationTab, clickUrl) {
    const installationMethodId = Object.prototype.hasOwnProperty.call(INSTALL_METHOD_LABELS, this.installMethodId) ? this.installMethodId : undefined;
    return aiAssetEngagement({
      aiAssetKind: this.isPlugin ? 'plugin' : 'skill',
      clickText,
      ...(clickUrl ? {
        clickUrl
      } : {}),
      elementTitle: this.isPlugin ? 'Install This Plugin' : 'Install This Skill',
      elementType,
      installationTab,
      ...(installationMethodId ? {
        installationMethodId
      } : {})
    });
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(GuidedInstallModal, {
  publicProps: {
    listing: {
      config: 0
    },
    installMethodId: {
      config: 0
    },
    mode: {
      config: 0
    },
    zipUrl: {
      config: 0
    }
  },
  publicMethods: ["show", "close"],
  wire: {
    i18n: {
      adapter: WireI18n,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["isOpen", "commandExpanded", "desktopRetryVisible", "topLayerUnavailable"]
});
const __lwc_component_class_internal = _registerComponent(GuidedInstallModal, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;